import { baseActions, baseMutations, baseState, BaseState, FilterParams } from "@/store/modules/helpers/baseState";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import {
    Subsets,
    subsetSetter,
    subsetGetter,
    subsetUpdateSetter,
    SubsetGetterProps,
} from "@/store/modules/helpers/baseSubset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";
import { Option } from "@/components/forms/inputs/Autocomplete/types";

export type TypAvGenomlysningsapparat = {
    id: number;
    namn: string;
    kod: string;
    ordning: number;
};

type State = BaseState & {
    typerAvGenomlysningsapparater: Map<TypAvGenomlysningsapparat["id"], TypAvGenomlysningsapparat>;
};

type Getters = {
    typAvGenomlysningsapparatById: (data: { id: number }) => TypAvGenomlysningsapparat | null;
};

// TODO: Ska den här verkligen vara duplicerad i enskilda moduler eller kan den importeras från baseState?
export type BaseActionsProps<T> = {
    state: BaseState & T;
    commit: <K>(
        event: "reset" | string,
        payload?: K,
        options?: {
            root?: boolean;
        }
    ) => void;
};

export const mapTypAvGenomlysningsapparat = (typAvGenomlysningsapparat: TypAvGenomlysningsapparat): Option => ({
    value: typAvGenomlysningsapparat.id,
    label: typAvGenomlysningsapparat.namn,
});

const getDefaultState: () => State = () => {
    return {
        typerAvGenomlysningsapparater: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "typAvGenomlysningsapparat";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        typerAvGenomlysningsapparater: (state: State) => [...state.typerAvGenomlysningsapparater.values()],
        typAvGenomlysningsapparatById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.typerAvGenomlysningsapparater.get(id);
            },
        typAvGenomlysningsapparatSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: "typerAvGenomlysningsapparater",
            }),
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addTyperAvGenomlysningsapparater: (
            state: State,
            {
                offset,
                subsetName = "default",
                typerAvGenomlysningsapparater = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                typerAvGenomlysningsapparater: TypAvGenomlysningsapparat[];
            }
        ) =>
            subsetSetter({
                offset,
                subsetName,
                state,
                collectionName: "typerAvGenomlysningsapparater",
                items: typerAvGenomlysningsapparater,
            }),
        updateTypAvGenomlysningsapparat: (
            state: State,
            {
                typerAvGenomlysningsapparater,
                offset,
                subsetName,
            }: {
                typerAvGenomlysningsapparater: TypAvGenomlysningsapparat[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, TypAvGenomlysningsapparat>({
                offset,
                state,
                collectionName: "typerAvGenomlysningsapparater",
                items: typerAvGenomlysningsapparater,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        getTypAvGenomlysningsapparatById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
            }: {
                id: number;
            }
        ) => {
            const foundTypAvGenomlysningsapparat = getters.typAvGenomlysningsapparatById({ id });

            if (foundTypAvGenomlysningsapparat || !id) {
                return foundTypAvGenomlysningsapparat;
            }

            try {
                const { data } = await klinikenApi.get<{
                    typAvGenomlysningsapparat: TypAvGenomlysningsapparat;
                }>(`operation/typeravgenomlysningsapparater/${id}`, getConfig({}));

                if (data) {
                    const retrievedTypAvGenomlysningsapparat = data;

                    commit("addTyperAvGenomlysningsapparater", {
                        offset: 0,
                        typerAvGenomlysningsapparater: [retrievedTypAvGenomlysningsapparat],
                        subsetName: id,
                    });

                    return retrievedTypAvGenomlysningsapparat;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta typer av genomlysningsapparater från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den skapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         * @param limit mängden objekt du vill få tillbaka i resultatet
         * @param page nummer för page. Används för att räkna ut offset som skickas till BE
         * @param filterParams alla sök och filtreringsparametrar. "ordering" är för sortering
         */
        fetchTyperAvGenomlysningsapparater: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams,
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
            }
        ) => {
            try {
                const { data } = await klinikenApi.get("operation/typeravgenomlysningsapparater/", getConfig());
                const results = data.results;

                const subsetName = "default";
                const total = 0;
                const nextPage = 0;
                const offset = 0;

                if (!results) {
                    return {
                        // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                        subsetName, // Totala mängden träffar från sökningen
                        total, // Nummer för nästa page
                        nextPage,
                    };
                }

                commit("addTyperAvGenomlysningsapparater", {
                    offset,
                    typerAvGenomlysningsapparater: results,
                });

                return {
                    // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                    subsetName, // Totala mängden träffar från sökningen
                    total, // Nummer för nästa page
                    nextPage,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createTypAvGenomlysningsapparat: async (
            { commit, state }: BaseActionsProps<State>,
            {
                typAvGenomlysningsapparat,
            }: {
                typAvGenomlysningsapparat: TypAvGenomlysningsapparat;
            }
        ) => {
            try {
                const response = await klinikenApi.post(
                    "operation/typeravgenomlysningsapparater/",
                    typAvGenomlysningsapparat,
                    getConfig({})
                );

                commit("addTyperAvGenomlysningsapparater", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    typerAvGenomlysningsapparater: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateTypAvGenomlysningsapparat: async (
            { commit, state }: BaseActionsProps<State>,
            {
                typAvGenomlysningsapparat,
            }: {
                typAvGenomlysningsapparat: TypAvGenomlysningsapparat;
            }
        ) => {
            try {
                const response = await klinikenApi.put(
                    `operation/typeravgenomlysningsapparater/${typAvGenomlysningsapparat.id}/`,
                    typAvGenomlysningsapparat,
                    getConfig({})
                );

                commit("updateTypAvGenomlysningsapparat", {
                    subsetName: "newlyUpdated",
                    typerAvGenomlysningsapparater: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
